import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { TranslationModule } from '@core/translation/translation.module';

/**
 * Two-or-more-option segmented control. Plain `<button>` elements with
 * Tailwind utilities — no PrimeNG, no SCSS. Variants change the color
 * scheme; the active option renders as a raised pill.
 */
export interface U2bSegmentedToggleOption<T> {
    /** Value emitted when the option is selected. Used as track key. */
    value: T;
    /** Material icon name shown left of the label. */
    icon: string;
    /** Tolgee key for the label. */
    labelKey: string;
}

export type U2bSegmentedToggleVariant =
    | 'default'      // Surface tones for normal page content
    | 'on-primary';  // White-on-darker-primary container for colored headers

@Component({
    selector: 'u2b-segmented-toggle',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, TranslationModule],
    host: {
        role: 'group',
        '[attr.aria-label]': 'ariaLabel() || null',
        '[attr.variant]': 'variant()',
        class: 'inline-flex items-center rounded-p-sm p-1 gap-1',
        '[class.bg-orange-700]': "variant() === 'on-primary'",
        '[class.bg-p-surface-100]': "variant() === 'default'",
    },
    template: `
        @for (option of options(); track option.value) {
            @let active = option.value === value();
            <button
                type="button"
                [attr.aria-pressed]="active"
                (click)="valueChange.emit(option.value)"
                [ngClass]="active
                    ? (variant() === 'on-primary'
                        ? 'bg-white text-orange-600 shadow'
                        : 'bg-p-primary text-p-primary-contrast shadow')
                    : (variant() === 'on-primary'
                        ? 'bg-transparent text-white hover:bg-orange-800'
                        : 'bg-transparent text-p-text-muted hover:bg-p-surface-200 hover:text-p-text')"
                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-p-sm border-0 text-u2b-base font-medium cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                <span class="material-icons text-u2b-base">{{ option.icon }}</span>
                <span>{{ option.labelKey | translate }}</span>
            </button>
        }
    `,
})
export class U2bSegmentedToggleComponent<T> {

    readonly options = input.required<readonly U2bSegmentedToggleOption<T>[]>();

    readonly value = input.required<T>();

    readonly variant = input<U2bSegmentedToggleVariant>('default');

    readonly ariaLabel = input<string>('');

    readonly valueChange = output<T>();
}
